// =============================================================================
// Pintravel — Sync status bar
// =============================================================================
// A slim bar above the tab bar, and only when there is something to say: no
// connection, changes waiting to go, or changes the server turned down. When
// everything is saved it is not there at all — a permanent "all good" icon in
// the header was noise on every screen.
//
// Tapping it: with rejected changes, opens them; otherwise, syncs now. It can
// be closed, until there is something new to say. Its height is published as
// --sync-bar-height so buttons floating above the tab bar clear it too.
// =============================================================================

import CloseIcon from '@mui/icons-material/Close';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, CircularProgress, Collapse, IconButton, Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { TFunction } from 'i18next';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { SyncState } from '@itravel/shared';

export interface SyncStatusIndicatorProps {
  syncState: SyncState;
  /** Tapped while changes are waiting: try to send them now. */
  onRequestSync?: () => void;
  /** Tapped while the server has turned changes down: show them. */
  onViewErrors?: () => void;
}

type Status = 'synced' | 'syncing' | 'pending' | 'offline' | 'error';

function deriveStatus(syncState: SyncState): Status {
  if (syncState.failedCount > 0) return 'error';
  if (!syncState.network.isOnline) return 'offline';
  if (syncState.pendingCount > 0) return syncState.isSyncing ? 'syncing' : 'pending';
  return 'synced';
}

function describe(
  status: Exclude<Status, 'synced'>,
  syncState: SyncState,
  t: TFunction,
): { text: string; color: 'info' | 'warning' | 'error'; icon: ReactNode } {
  const { pendingCount, failedCount } = syncState;
  switch (status) {
    case 'syncing':
      return {
        text: t('sync.status.syncingDescription', { count: pendingCount }),
        color: 'info',
        icon: <CloudSyncIcon fontSize="small" />,
      };
    case 'pending':
      return {
        text: t('sync.status.pendingDescription', { count: pendingCount }),
        color: 'warning',
        icon: <CloudSyncIcon fontSize="small" />,
      };
    case 'offline':
      return {
        text:
          pendingCount > 0
            ? t('sync.status.pendingDescription', { count: pendingCount })
            : t('sync.status.offlineDescription'),
        color: 'info',
        icon: <CloudOffIcon fontSize="small" />,
      };
    case 'error':
      return {
        text: t('sync.status.errorDescription', { count: failedCount }),
        color: 'error',
        icon: <ErrorOutlineIcon fontSize="small" />,
      };
  }
}

function formatLastSynced(iso: string, t: TFunction): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 10) return t('sync.justNow');
  if (seconds < 60) return t('sync.secondsAgo', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('sync.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('sync.hoursAgo', { count: hours });
  return t('sync.daysAgo', { count: Math.floor(hours / 24) });
}

export function SyncStatusIndicator({
  syncState,
  onRequestSync,
  onViewErrors,
}: SyncStatusIndicatorProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const status = deriveStatus(syncState);
  // Rejected changes stay: they wait on the user and do not resolve themselves.
  const [dismissed, setDismissed] = useState<Status | null>(null);
  const visible = status !== 'synced' && (status === 'error' || dismissed !== status);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = barRef.current;
    if (!element) return;
    const root = document.documentElement.style;
    // Follows the collapse frame by frame, so the buttons above move with it.
    const observer = new ResizeObserver(() =>
      root.setProperty('--sync-bar-height', `${element.offsetHeight}px`),
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
      root.setProperty('--sync-bar-height', '0px');
    };
  }, []);
  // Kept for the collapse out: the last thing it said, not "synced".
  const shown = visible ? describe(status, syncState, t) : null;
  const action =
    status === 'error' ? onViewErrors : status === 'pending' ? onRequestSync : undefined;

  const main = shown ? theme.palette[shown.color].main : theme.palette.info.main;
  const textColor = shown ? theme.palette[shown.color].dark : theme.palette.info.dark;

  return (
    <Collapse in={visible} ref={barRef}>
      {shown && (
        <Paper
          elevation={0}
          role={action ? 'button' : 'status'}
          tabIndex={action ? 0 : undefined}
          onClick={action}
          sx={{
            px: 2,
            py: 0.9,
            borderRadius: 0,
            bgcolor: alpha(main, 0.12),
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            cursor: action ? 'pointer' : 'default',
          }}
        >
          <Box sx={{ color: textColor, display: 'flex' }}>
            {status === 'syncing' ? (
              <CircularProgress size={16} thickness={4} sx={{ color: textColor }} />
            ) : (
              shown.icon
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: textColor }}>
              {shown.text}
            </Typography>
            {syncState.lastSyncedAt && status === 'offline' && (
              <Typography sx={{ fontSize: 11.5, color: alpha(textColor, 0.75) }}>
                {t('sync.lastSynced', { time: formatLastSynced(syncState.lastSyncedAt, t) })}
              </Typography>
            )}
          </Box>
          {action && (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: textColor,
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
              }}
            >
              {status === 'error' ? t('sync.viewDetails') : t('sync.syncNow')}
            </Typography>
          )}
          {status !== 'error' && (
            <IconButton
              size="small"
              aria-label={t('common.close')}
              onClick={(event) => {
                event.stopPropagation();
                setDismissed(status);
              }}
              sx={{ color: textColor, ml: -0.5, mr: -1 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Paper>
      )}
    </Collapse>
  );
}

export default SyncStatusIndicator;
